import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Logo } from './Logo';
import { Menu, X } from 'lucide-react';

export const Header = ({ t, lang, onLanguageChange, onNavigate }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'home', section: 'hero' },
    { key: 'about', section: 'about' },
    { key: 'properties', section: 'properties' },
    { key: 'services', section: 'services' },
    { key: 'contact', section: 'contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-black/90 backdrop-blur-2xl border-b border-white/10 shadow-2xl shadow-black/50'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer group" onClick={() => onNavigate('hero')}>
            <Logo size="md" showText={true} className="transition-transform duration-300 group-hover:scale-105" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => onNavigate(item.section)}
                className="relative px-5 py-2.5 text-sm font-semibold text-gray-300 hover:text-white transition-all duration-300 group"
              >
                <span className="relative z-10">{t.nav[item.key]}</span>
                {/* Hover background effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#C5A05E]/0 via-[#C5A05E]/20 to-[#C5A05E]/0 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-300" />
                {/* Bottom indicator */}
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-transparent via-[#C5A05E] to-transparent group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </nav>

          {/* Language Switcher & Admin Button */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher currentLang={lang} onLanguageChange={onLanguageChange} />
            <Button
              onClick={() => window.location.href = '/admin'}
              className="relative overflow-hidden bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] hover:from-[#D4B06E] hover:to-[#C5A05E] text-white font-semibold px-6 py-2.5 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-[#C5A05E]/30 hover:shadow-[#C5A05E]/50 group"
            >
              <span className="relative z-10">{t.nav.admin}</span>
              <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white hover:text-[#C5A05E] transition-colors relative z-50"
          >
            <div className="relative w-6 h-6">
              <span className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${isMobileMenuOpen ? 'rotate-90 opacity-0' : 'rotate-0 opacity-100'}`}>
                <Menu size={24} />
              </span>
              <span className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${isMobileMenuOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0'}`}>
                <X size={24} />
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 backdrop-blur-2xl bg-black/95 border-b border-white/10 overflow-hidden transition-all duration-500 ${isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-4 py-6 space-y-2">
          {navItems.map((item, index) => (
            <button
              key={item.key}
              onClick={() => {
                onNavigate(item.section);
                setIsMobileMenuOpen(false);
              }}
              className="block w-full text-left px-6 py-4 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-300 border border-transparent hover:border-[#C5A05E]/30"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {t.nav[item.key]}
            </button>
          ))}
          <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-4">
            <LanguageSwitcher currentLang={lang} onLanguageChange={onLanguageChange} />
            <Button
              onClick={() => window.location.href = '/admin'}
              className="bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] hover:from-[#D4B06E] hover:to-[#C5A05E] text-white font-semibold"
              size="sm"
            >
              {t.nav.admin}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};